import React, { useEffect, useMemo, useState } from 'react'
import objectHash from 'object-hash'
import browserCache from '~/prix/utils/browserCache'
import { EntityKey } from '../../entity'
import { AppError } from '../../error'
import { SerializableQuery, Selectables, SelectablesToItem } from '../../query'
import resolve from '../../resolve'
import useAPI from './api'
import { v4 as generateUUID } from 'uuid'

export default function useItems<
  MainEntityKey extends EntityKey,
  MainEntityKeyAlias extends string,
  Selection extends Selectables,
>(
  queryFactory: () => SerializableQuery<MainEntityKey, MainEntityKeyAlias, Selection>,
  deps: React.DependencyList,
  {
    autoLoad = true,
    cache,
  }: {
    autoLoad?: boolean
    /**
     * Cache in seconds
     */
    cache?: number
  } = {},
) {
  const api = useAPI()
  const query = useMemo(queryFactory, deps)
  const [items, setItems] = useState<SelectablesToItem<Selection>[]>()
  const [metadata, setMetadata] = useState({})
  const [error, setError] = useState<Error | AppError>()
  const [isLoading, setIsLoading] = useState(autoLoad)
  const requestId = useMemo(() => {
    const generatedId = generateUUID()
    return generatedId
  }, [api, query, autoLoad])

  const load = () => {
    setError(undefined)
    setIsLoading(true)

    const hash = cache
      ? objectHash(query, {
          respectType: false,
          respectFunctionProperties: false,
        })
      : null
    const cacheName = `${query?.entityKey}.${hash}`

    let abort: () => Promise<void> = async () => undefined
    const resultPromise = browserCache(
      {
        bypass: !cache,
        name: cacheName,
        expirationSeconds: cache || 0,
        args: [],
      },
      async () => {
        api.context.requestId = requestId
        api.context.debugFiles = query.callStack?.join(', ')
        const { abort: newAbort, run } = resolve({
          ...api,
          query,
          onAbort: () => {
            setIsLoading(false)
            setItems(undefined)
            setError(undefined)
          },
        })
        abort = newAbort

        const { firstResponse } = await run()
        return firstResponse
      },
    )
      .then(result => {
        const { items, metadata } = result
        if (items === undefined || (result.status && result.status !== 200)) {
          return result
        }
        setMetadata(metadata)
        setItems(items)
        setError(undefined)
        setIsLoading(false)
        return result
      })
      .catch(error => setError(error))
      .finally(() => setIsLoading(false))

    return {
      abort,
      resultPromise,
    }
  }

  useEffect(() => {
    if (!autoLoad) {
      return
    }

    const loadResult = load()
    return () => {
      loadResult.abort()
    }
  }, [api, query, autoLoad])

  return {
    metadata,
    items,
    error,
    isLoading,
    query,
    load,
  }
}
